import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { vectorService } from '../services/vectorService';

/**
 * Async thunk for vector search
 */
export const vectorSearch = createAsyncThunk(
  'vector/search',
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        query,
        collectionName = 'claims',
        topK = 10,
        page = 1,
        limit = 10,
        filters = {}
      } = params;

      const response = await vectorService.searchClaimsWithPagination({
        query,
        collectionName,
        topK,
        page,
        limit,
        filters
      });

      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Vector search failed');
    }
  }
);

// Initial state
const initialState = {
  results: [],
  query: '',
  collectionName: 'claims',
  totalResults: 0,
  page: 1,
  limit: 10,
  loading: false,
  error: null,
  searchMetadata: {
    executionTimeMs: 0,
    vectorSearchUsed: true,
    searchType: 'vector'
  }
};

/**
 * Vector search slice
 * Manages vector search state separately from traditional filters
 */
const vectorSlice = createSlice({
  name: 'vector',
  initialState,
  reducers: {
    /**
     * Clear vector search results and reset to initial state
     */
    clearVectorSearch: (state) => {
      state.results = [];
      state.query = '';
      state.page = 1;
      state.totalResults = 0;
      state.error = null;
    },

    /**
     * Reset to initial state
     */
    resetVectorState: (state) => {
      return initialState;
    },

    /**
     * Set the current page for pagination
     */
    setVectorPage: (state, action) => {
      state.page = action.payload;
    },

    /**
     * Clear any vector search errors
     */
    clearVectorError: (state) => {
      state.error = null;
    }
  },

  extraReducers: (builder) => {
    builder
      // Vector search pending
      .addCase(vectorSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      // Vector search fulfilled
      .addCase(vectorSearch.fulfilled, (state, action) => {
        state.loading = false;
        
        const payload = action.payload || {};
        
        // Handle backend response structure: { data: { documents: [...] } }
        const backendData = payload.data || {};
        const documents = backendData.documents || [];
        
        // Extract claim IDs from vector results
        const claimIds = documents.map(doc => doc.id);

        if (claimIds.length === 0) {
          state.results = [];
          state.totalResults = 0;
          state.documents = [];
          state.searchMetadata = {
            executionTimeMs: 0,
            vectorSearchUsed: true,
            searchType: 'vector'
          };
          return;
        }

        // For now, store the documents as results (since they contain claim data)
        // Later, you can fetch full claims using the IDs if needed
        state.results = documents; // These are the claim objects from backend
        state.totalResults = documents.length;
        state.documents = documents; // Store vector metadata
        
        state.searchMetadata = {
          executionTimeMs: backendData.executionTimeMs || 0,
          vectorSearchUsed: true,
          searchType: 'vector',
          queryExecutedOn: new Date().toISOString()
        };

        // Update pagination info if provided
        if (backendData.pagination) {
          state.page = backendData.pagination.page || 1;
          state.totalResults = backendData.pagination.totalItems || documents.length;
        }
      })

      // Vector search rejected
      .addCase(vectorSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || 'Vector search failed';
        state.results = [];
      });
  }
});

export const {
  clearVectorSearch,
  resetVectorState,
  setVectorPage,
  clearVectorError
} = vectorSlice.actions;

export default vectorSlice.reducer;